/**
 * ComponenteUploadPeticaoInicial - Upload de Petição Inicial com WebSocket em Tempo Real
 *
 * CONTEXTO DE NEGÓCIO:
 * Componente especializado para upload da petição inicial no fluxo de análise de petição.
 * Diferente do upload tradicional, aceita apenas 1 arquivo e dispara automaticamente
 * a análise de documentos após processamento completo.
 *
 * FUNCIONALIDADES:
 * - Drag-and-drop de arquivo único (PDF ou DOCX)
 * - Validação client-side (tipo, tamanho máx 20MB)
 * - Upload assíncrono com WebSocket para atualizações em tempo real
 * - Progress bar com etapas detalhadas (Salvando, Extraindo, OCR, Vetorizando)
 * - Disparo automático de análise de documentos após upload
 * - Feedback visual em tempo real via WebSocket
 *
 * VANTAGENS SOBRE POLLING:
 * - Atualizações instantâneas (sem delay de 2s)
 * - Menos requests HTTP desnecessários
 * - Melhor experiência para uploads longos
 * - Timeout aumentado para 60 minutos
 *
 * TIPOS ACEITOS:
 * - PDF (texto ou escaneado)
 * - DOCX (Microsoft Word)
 *
 * VALIDAÇÕES:
 * - Tamanho máximo: 20MB
 * - Extensões permitidas: .pdf, .docx
 * - Apenas 1 arquivo por vez
 *
 * PADRÃO ASSÍNCRONO COM WEBSOCKET (TAREFA-050):
 * 1. POST /api/peticoes/iniciar retorna peticao_id + upload_id (<100ms)
 * 2. WebSocket /ws/documentos/status para atualizações em tempo real
 * 3. Quando upload completo, dispara POST /api/peticoes/{peticao_id}/analisar-documentos
 * 4. Polling simples para análise de documentos (GET /api/peticoes/{peticao_id}/status)
 * 5. Quando documentos sugeridos aparecerem, habilita botão "Avançar"
 *
 * USO:
 * ```tsx
 * <ComponenteUploadPeticaoInicial
 *   aoConcluirComSucesso={(peticaoId, documentosSugeridos) => {
 *     console.log('Petição criada:', peticaoId);
 *     console.log('Documentos sugeridos:', documentosSugeridos);
 *   }}
 *   aoOcorrerErro={(erro) => console.error(erro)}
 * />
 * ```
 *
 * NOTA PARA LLMs:
 * Este componente faz parte da TAREFA-050 - Componente de Upload de Petição Inicial.
 * Reutiliza lógica de upload assíncrono da TAREFA-038, mas especializado para petições.
 * Agora usa WebSocket para atualizações em tempo real ao invés de polling HTTP.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, FileText, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import {
  obterResultadoUpload,
} from '../../servicos/servicoApiDocumentos';
import {
  iniciarPeticao,
  analisarDocumentos,
  verificarStatusPeticao,
} from '../../servicos/servicoApiPeticoes';
import type { DocumentoSugerido } from '../../tipos/tiposPeticao';
import { formatarTamanhoArquivo, obterExtensaoArquivo } from '../../tipos/tiposDocumentos';

// WebSocket para atualizações em tempo real
import { obterWebSocketUploadService, type AtualizacaoStatusUpload } from '../../../../core/servicos/websocketUploadService';

// ===== CONSTANTES =====

/**
 * Tamanho máximo permitido para petição inicial: 20MB
 * 
 * JUSTIFICATIVA:
 * Petições iniciais tendem a ser documentos menores que laudos/processos completos.
 * 20MB é suficiente para PDFs de até ~200 páginas.
 */
const TAMANHO_MAXIMO_PETICAO_MB = 20;

/**
 * Tamanho máximo permitido para arquivo ZIP: 100MB
 */
const TAMANHO_MAXIMO_ZIP_MB = 100;

/**
 * Extensões permitidas para petição inicial
 */
const EXTENSOES_PERMITIDAS_PETICAO = ['.pdf', '.docx'];

/**
 * Extensões permitidas para arquivo ZIP
 */
const EXTENSOES_PERMITIDAS_ZIP = ['.zip'];

/**
 * MIME types aceitos para petição
 */
const TIPOS_MIME_ACEITOS_PETICAO = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

/**
 * MIME types aceitos para ZIP
 */
const TIPOS_MIME_ACEITOS_ZIP = [
  'application/zip',
  'application/x-zip-compressed',
];

/**
 * Timeout máximo para polling de upload (60 minutos - para arquivos ZIP muito grandes)
 */
const TIMEOUT_POLLING_UPLOAD_MS = 60 * 60 * 1000;

// ===== INTERFACES =====

/**
 * Tipo para arquivo ignorado
 */
export interface ArquivoIgnorado {
  nome_arquivo: string;
  extensao: string;
  motivo: string;
}

/**
 * Propriedades do ComponenteUploadPeticaoInicial
 */
interface PropriedadesComponenteUploadPeticaoInicial {
  /**
   * Callback chamado quando upload E análise de documentos forem concluídos com sucesso
   * 
   * @param peticaoId - UUID da petição criada no backend
   * @param documentosSugeridos - Lista de documentos sugeridos pela LLM
   * @param arquivosIgnorados - Lista de arquivos ignorados por tipo não suportado (opcional)
   */
  aoConcluirComSucesso?: (peticaoId: string, documentosSugeridos: DocumentoSugerido[], arquivosIgnorados?: ArquivoIgnorado[]) => void;

  /**
   * Callback chamado se houver erro em qualquer etapa
   * 
   * @param mensagemErro - Descrição do erro ocorrido
   */
  aoOcorrerErro?: (mensagemErro: string) => void;

  /**
   * Tipo de ação jurídica (opcional)
   * Se não informado, pode ser inferido pela LLM posteriormente
   */
  tipoAcao?: string;
}

/**
 * Status do componente
 */
type StatusComponente =
  | 'aguardando_selecao'    // Esperando usuário selecionar arquivo
  | 'validando'              // Validando arquivo selecionado
  | 'enviando'               // Fazendo upload inicial (POST /iniciar)
  | 'processando_upload'     // Polling de upload (0-100%)
  | 'analisando_documentos'  // Polling de análise de documentos
  | 'concluido'              // Tudo pronto, documentos sugeridos disponíveis
  | 'erro';                  // Erro em alguma etapa

/**
 * Modo de upload
 */
type ModoUpload = 'peticao' | 'zip';

// ===== COMPONENTE PRINCIPAL =====

export function ComponenteUploadPeticaoInicial({
  aoConcluirComSucesso,
  aoOcorrerErro,
  tipoAcao,
}: PropriedadesComponenteUploadPeticaoInicial): JSX.Element {
  // ===== ESTADO =====
  
  /**
   * Status atual do componente
   */
  const [status, setStatus] = useState<StatusComponente>('aguardando_selecao');
  
  /**
   * Modo de upload selecionado
   */
  const [modoUpload, setModoUpload] = useState<ModoUpload>('peticao');
  
  /**
   * Arquivo selecionado pelo usuário
   */
  const [arquivoSelecionado, setArquivoSelecionado] = useState<File | null>(null);
  
  /**
   * Etapa atual do processamento (ex: "Extraindo texto", "Vetorizando")
   */
  const [etapaAtual, setEtapaAtual] = useState<string>('');

  /**
   * Progresso do upload (0-100)
   */
  const [progressoUpload, setProgressoUpload] = useState<number>(0);

  /**
   * Mensagem de erro
   */
  const [mensagemErro, setMensagemErro] = useState<string>('');
  
  /**
   * Erros de validação
   */
  const [errosValidacao, setErrosValidacao] = useState<string[]>([]);
  
  /**
   * Lista de arquivos extraídos (apenas para uploads ZIP)
   */
  const [arquivosExtraidos, setArquivosExtraidos] = useState<string[]>([]);
  
  /**
   * Lista de arquivos ignorados por tipo não suportado (para uploads ZIP)
   */
  const [arquivosIgnorados, setArquivosIgnorados] = useState<Array<{ nome_arquivo: string; extensao: string; motivo: string }>>([]);
  
  /**
   * Progresso individual por arquivo (para uploads ZIP)
   */
  const [progressoPorArquivo, setProgressoPorArquivo] = useState<Record<string, { progresso: number; etapa: string }>>({});
  
  /**
   * Ref para armazenar intervalos de polling
   */
  const intervaloUploadRef = useRef<number | null>(null);
  const intervaloAnaliseRef = useRef<number | null>(null);
  
  /**
   * Ref para função de unsubscribe do WebSocket
   */
  const unsubscribeWebSocketRef = useRef<(() => void) | null>(null);
  
  /**
   * Ref para timeout de polling (evitar polling infinito)
   */
  const timeoutUploadRef = useRef<number | null>(null);
  const timeoutAnaliseRef = useRef<number | null>(null);
  const uploadIdRef = useRef<string | null>(null);

  // ===== FUNÇÕES AUXILIARES =====

  /**
   * Valida arquivo selecionado
   * 
   * VALIDAÇÕES:
   * - Tipo de arquivo (PDF/DOCX ou ZIP conforme modo)
   * - Tamanho (máx conforme modo)
   * 
   * @param arquivo - Arquivo a validar
   * @param modo - Modo de upload
   * @returns Lista de erros (vazia se válido)
   */
  const validarArquivo = useCallback((arquivo: File, modo: ModoUpload): string[] => {
    const erros: string[] = [];
    
    // Selecionar constantes baseadas no modo
    const extensoesPermitidas = modo === 'peticao' ? EXTENSOES_PERMITIDAS_PETICAO : EXTENSOES_PERMITIDAS_ZIP;
    const tiposMimeAceitos = modo === 'peticao' ? TIPOS_MIME_ACEITOS_PETICAO : TIPOS_MIME_ACEITOS_ZIP;
    const tamanhoMaximoMB = modo === 'peticao' ? TAMANHO_MAXIMO_PETICAO_MB : TAMANHO_MAXIMO_ZIP_MB;
    
    // Validar extensão
    const extensao = obterExtensaoArquivo(arquivo.name);
    if (!extensoesPermitidas.includes(extensao)) {
      erros.push(
        `Tipo de arquivo não permitido: ${extensao}. Apenas ${extensoesPermitidas.join(', ')} são aceitos.`
      );
    }
    
    // Validar MIME type
    if (!tiposMimeAceitos.includes(arquivo.type)) {
      erros.push(
        `Tipo MIME não permitido: ${arquivo.type}. Apenas ${tiposMimeAceitos.join(', ')} são aceitos.`
      );
    }
    
    // Validar tamanho
    const tamanhoMB = arquivo.size / (1024 * 1024);
    if (tamanhoMB > tamanhoMaximoMB) {
      erros.push(
        `Arquivo muito grande: ${tamanhoMB.toFixed(1)}MB. Tamanho máximo: ${tamanhoMaximoMB}MB.`
      );
    }
    
    return erros;
  }, []);

  /**
   * Limpa todos os intervalos e timeouts ativos
   * 
   * CONTEXTO:
   * Previne memory leaks ao desmontar componente ou resetar estado.
   */
  const limparPollings = useCallback(() => {
    if (intervaloUploadRef.current) {
      clearInterval(intervaloUploadRef.current);
      intervaloUploadRef.current = null;
    }
    
    if (intervaloAnaliseRef.current) {
      clearInterval(intervaloAnaliseRef.current);
      intervaloAnaliseRef.current = null;
    }
    
    if (timeoutUploadRef.current) {
      clearTimeout(timeoutUploadRef.current);
      timeoutUploadRef.current = null;
    }
    
    if (timeoutAnaliseRef.current) {
      clearTimeout(timeoutAnaliseRef.current);
      timeoutAnaliseRef.current = null;
    }

    // Limpar WebSocket
    if (unsubscribeWebSocketRef.current) {
      unsubscribeWebSocketRef.current();
      unsubscribeWebSocketRef.current = null;
    }

    // Desconectar WebSocket fisicamente
    const servicoWebSocket = obterWebSocketUploadService(undefined, uploadIdRef.current || undefined);
    servicoWebSocket.disconnect();
  }, []);

  /**
   * Reseta estado do componente
   */
  const resetarEstado = useCallback(() => {
    limparPollings();
    setStatus('aguardando_selecao');
    setArquivoSelecionado(null);
    setEtapaAtual('');
    setProgressoUpload(0);
    setMensagemErro('');
    setErrosValidacao([]);
    setArquivosExtraidos([]);
    setArquivosIgnorados([]);
    setProgressoPorArquivo({});
  }, [limparPollings]);

  /**
   * Inicia monitoramento de análise de documentos via WebSocket
   *
   * NOTA: Como a análise de documentos não tem WebSocket específico,
   * mantemos polling simples para esta parte, mas com timeout reduzido.
   *
   * @param peticaoId - ID da petição sendo analisada
   */
  const iniciarMonitoramentoAnaliseWebSocket = useCallback((peticaoId: string) => {
    console.log('[ComponenteUploadPeticaoInicial] Iniciando monitoramento de análise via polling (WebSocket não disponível para análise)');

    // Limpar polling anterior
    if (intervaloAnaliseRef.current) {
      clearInterval(intervaloAnaliseRef.current);
    }

    // Configurar timeout reduzido (2 minutos para análise)
    timeoutAnaliseRef.current = window.setTimeout(() => {
      limparPollings();
      setStatus('erro');
      setMensagemErro('Timeout: Análise de documentos demorou muito tempo (>2min). Tente novamente.');
      aoOcorrerErro?.('Timeout de análise de documentos');
    }, 2 * 60 * 1000); // 2 minutos

    // Iniciar polling simples para análise
    intervaloAnaliseRef.current = window.setInterval(async () => {
      try {
        const resposta = await verificarStatusPeticao(peticaoId);
        const { documentos_sugeridos } = resposta.data;

        console.log('[ComponenteUploadPeticaoInicial] Verificando documentos sugeridos...', documentos_sugeridos);

        // Verificar se documentos já foram sugeridos
        if (documentos_sugeridos && documentos_sugeridos.length > 0) {
          console.log('[ComponenteUploadPeticaoInicial] Documentos sugeridos recebidos!', documentos_sugeridos);

          // Parar polling
          limparPollings();

          // Atualizar estado
          setStatus('concluido');
          setEtapaAtual('Análise concluída');
          setProgressoUpload(100);

          // Chamar callback de sucesso passando também os arquivos ignorados
          aoConcluirComSucesso?.(peticaoId, documentos_sugeridos, arquivosIgnorados);
        }
      } catch (erro) {
        console.error('[ComponenteUploadPeticaoInicial] Erro ao verificar status de petição:', erro);

        limparPollings();
        setStatus('erro');
        setMensagemErro('Erro ao verificar status de análise. Tente novamente.');
        aoOcorrerErro?.('Erro ao verificar status de análise');
      }
    }, 2000); // A cada 2 segundos
  }, [limparPollings, aoOcorrerErro, aoConcluirComSucesso, arquivosIgnorados]);

  // ===== WEBSOCKET PARA ATUALIZAÇÕES EM TEMPO REAL =====

  /**
   * Configura WebSocket para receber atualizações de status em tempo real
   *
   * FUNCIONALIDADES:
   * - Conecta ao WebSocket do backend
   * - Inscreve callback para processar atualizações
   * - Trata desconexão automática no cleanup
   *
   * @param uploadId - ID do upload para filtrar mensagens relevantes
   * @param peticaoId - ID da petição para análise de documentos
   */
  const configurarWebSocket = useCallback((uploadId: string, peticaoId: string) => {
    console.log('[ComponenteUploadPeticaoInicial] Configurando WebSocket para upload:', uploadId, 'petição:', peticaoId);

    // Atualizar ref com uploadId atual
    uploadIdRef.current = uploadId;

    // Limpar inscrição anterior se existir
    if (unsubscribeWebSocketRef.current) {
      unsubscribeWebSocketRef.current();
      unsubscribeWebSocketRef.current = null;
    }

    // Obter instância do serviço WebSocket
    const servicoWebSocket = obterWebSocketUploadService(undefined, uploadId);

    // Conectar ao WebSocket
    servicoWebSocket.connect();

    // Inscrever para receber atualizações
    const unsubscribe = servicoWebSocket.subscribe((atualizacao: AtualizacaoStatusUpload) => {
      console.log('[ComponenteUploadPeticaoInicial] 📨 Atualização WebSocket recebida:', atualizacao);

      // Filtrar apenas mensagens relevantes para este upload/petição
      if (atualizacao.upload_id !== uploadId) {
        console.log('[ComponenteUploadPeticaoInicial] Ignorando atualização para outro upload:', atualizacao.upload_id);
        return;
      }

      // Processar atualização baseada no status
      const { status, etapa_atual, progresso_percentual, mensagem_erro, arquivos_extraidos, arquivos_ignorados, progresso_por_arquivo } = atualizacao;

      // Atualizar UI
      setEtapaAtual(etapa_atual || '');
      setProgressoUpload(progresso_percentual || 0);
      if (arquivos_extraidos) {
        setArquivosExtraidos(arquivos_extraidos);
      }
      if (arquivos_ignorados) {
        setArquivosIgnorados(arquivos_ignorados);
      }
      if (progresso_por_arquivo) {
        setProgressoPorArquivo(progresso_por_arquivo);
      }

      console.log('[ComponenteUploadPeticaoInicial] Estado WebSocket atualizado - etapa:', etapa_atual, 'progresso:', progresso_percentual, 'arquivos_extraidos:', arquivos_extraidos, 'arquivos_ignorados:', arquivos_ignorados, 'progresso_por_arquivo:', progresso_por_arquivo);

      // Verificar se concluído
      if (status === 'CONCLUIDO') {
        console.log('[ComponenteUploadPeticaoInicial] Upload concluído via WebSocket! Disparando análise de documentos...');

        // Log lista de arquivos extraídos se for ZIP
        if (arquivos_extraidos) {
          console.log('[ComponenteUploadPeticaoInicial] Arquivos extraídos:', arquivos_extraidos);
        }

        // Limpar timeouts (não há mais polling)
        if (timeoutUploadRef.current) {
          clearTimeout(timeoutUploadRef.current);
          timeoutUploadRef.current = null;
        }

        // Obter resultado do upload
        obterResultadoUpload(uploadId).then(() => {
          // Disparar análise de documentos
          setStatus('analisando_documentos');
          setEtapaAtual('Analisando petição e sugerindo documentos...');
          setProgressoUpload(0);

          analisarDocumentos(peticaoId).then(() => {
            // Iniciar monitoramento de análise via WebSocket (simular polling)
            iniciarMonitoramentoAnaliseWebSocket(peticaoId);
          }).catch((erro) => {
            console.error('[ComponenteUploadPeticaoInicial] Erro ao analisar documentos:', erro);
            setStatus('erro');
            setMensagemErro('Erro ao iniciar análise de documentos. Tente novamente.');
            aoOcorrerErro?.('Erro ao analisar documentos');
          });
        }).catch((erro) => {
          console.error('[ComponenteUploadPeticaoInicial] Erro ao obter resultado upload:', erro);
          setStatus('erro');
          setMensagemErro('Erro ao obter resultado do upload. Tente novamente.');
          aoOcorrerErro?.('Erro ao obter resultado do upload');
        });
      }

      // Verificar se houve erro
      if (status === 'ERRO') {
        console.error('[ComponenteUploadPeticaoInicial] Erro no upload via WebSocket:', mensagem_erro);

        // Limpar timeouts e desconectar WebSocket
        if (timeoutUploadRef.current) {
          clearTimeout(timeoutUploadRef.current);
          timeoutUploadRef.current = null;
        }

        if (unsubscribeWebSocketRef.current) {
          unsubscribeWebSocketRef.current();
          unsubscribeWebSocketRef.current = null;
        }

        servicoWebSocket.disconnect();

        setStatus('erro');
        setMensagemErro(mensagem_erro || 'Erro desconhecido ao processar upload');
        aoOcorrerErro?.(mensagem_erro || 'Erro no upload');
      }
    });

    // Armazenar função de unsubscribe para cleanup
    unsubscribeWebSocketRef.current = unsubscribe;

    console.log('[ComponenteUploadPeticaoInicial] WebSocket configurado e conectado');
  }, [aoOcorrerErro, iniciarMonitoramentoAnaliseWebSocket]);



  // ===== POLLING DE UPLOAD =====

  /**
   * Inicia polling de status de upload
   * 
   * FLUXO:
   * 1. A cada 2s, chama GET /api/documentos/status-upload/{upload_id}
   * 2. Atualiza progresso e etapa atual na UI
   * 3. Se status = CONCLUIDO → Para polling e dispara análise de documentos
   * 4. Se status = ERRO → Para polling e exibe erro
   * 
   * @param uploadIdParam - ID do upload
   * @param peticaoIdParam - ID da petição
   */
  const iniciarPollingUpload = useCallback((uploadIdParam: string, peticaoIdParam: string) => {
    console.log('[ComponenteUploadPeticaoInicial] Iniciando monitoramento WebSocket de upload:', uploadIdParam, 'Petição:', peticaoIdParam);

    // Configurar timeout máximo (60 minutos) - mantido como segurança
    timeoutUploadRef.current = window.setTimeout(() => {
      // Limpar WebSocket e timeouts
      if (unsubscribeWebSocketRef.current) {
        unsubscribeWebSocketRef.current();
        unsubscribeWebSocketRef.current = null;
      }
      if (timeoutUploadRef.current) {
        clearTimeout(timeoutUploadRef.current);
        timeoutUploadRef.current = null;
      }

      // Desconectar WebSocket
      const servicoWebSocket = obterWebSocketUploadService(undefined, uploadIdRef.current || undefined);
      servicoWebSocket.disconnect();

      setStatus('erro');
      setMensagemErro('Timeout: Upload demorou muito tempo (>60min). Tente novamente.');
      aoOcorrerErro?.('Timeout de upload');
    }, TIMEOUT_POLLING_UPLOAD_MS);

    // Configurar WebSocket para receber atualizações em tempo real
    configurarWebSocket(uploadIdParam, peticaoIdParam);
  }, [configurarWebSocket, aoOcorrerErro]);

  // ===== HANDLERS =====

  /**
   * Handler chamado quando arquivo é selecionado via drop ou clique
   * 
   * FLUXO:
   * 1. Validar arquivo
   * 2. Se válido → Fazer upload (POST /api/peticoes/iniciar)
   * 3. Iniciar polling de upload
   */
  const aoSelecionarArquivo = useCallback(
    async (arquivos: File[]) => {
      // Resetar estado anterior
      setErrosValidacao([]);
      setMensagemErro('');
      
      // Aceitar apenas 1 arquivo
      if (arquivos.length === 0) {
        return;
      }
      
      if (arquivos.length > 1) {
        setErrosValidacao(['Selecione apenas 1 arquivo por vez.']);
        return;
      }
      
      const arquivo = arquivos[0];
      
      // Validar arquivo
      const erros = validarArquivo(arquivo, modoUpload);
      if (erros.length > 0) {
        setErrosValidacao(erros);
        return;
      }
      
      // Arquivo válido - salvar no estado
      setArquivoSelecionado(arquivo);
      setStatus('enviando');
      setEtapaAtual('Enviando petição inicial...');
      
      try {
        // Fazer upload inicial
        const respostaInicio = await iniciarPeticao(arquivo, tipoAcao);
        const { peticao_id, upload_id } = respostaInicio.data;
        
        console.log('[ComponenteUploadPeticaoInicial] Petição criada:', peticao_id, 'Upload ID:', upload_id);
        
        // Iniciar polling de upload
        setStatus('processando_upload');
        iniciarPollingUpload(upload_id, peticao_id);
      } catch (erro: unknown) {
        console.error('[ComponenteUploadPeticaoInicial] Erro ao iniciar upload:', erro);
        
        const erroAxios = erro as { response?: { data?: { detail?: string } } };
        setStatus('erro');
        setMensagemErro(
          erroAxios.response?.data?.detail || 'Erro ao enviar arquivo. Tente novamente.'
        );
        aoOcorrerErro?.(erroAxios.response?.data?.detail || 'Erro ao enviar arquivo');
      }
    },
    [validarArquivo, modoUpload, tipoAcao, iniciarPollingUpload, aoOcorrerErro]
  );

  /**
   * Handler para remover arquivo selecionado
   */
  const removerArquivo = useCallback(() => {
    resetarEstado();
  }, [resetarEstado]);

  // ===== DROPZONE =====

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
  } = useDropzone({
    onDrop: aoSelecionarArquivo,
    accept: modoUpload === 'peticao' 
      ? {
          'application/pdf': ['.pdf'],
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        }
      : {
          'application/zip': ['.zip'],
        },
    maxFiles: 1,
    maxSize: modoUpload === 'peticao' ? TAMANHO_MAXIMO_PETICAO_MB * 1024 * 1024 : 100 * 1024 * 1024, // 100MB for ZIP
    disabled: status !== 'aguardando_selecao',
  });

  // ===== CLEANUP =====

  /**
   * Limpar intervalos ao desmontar componente
   */
  useEffect(() => {
    return () => {
      limparPollings();
    };
  }, [limparPollings]);

  return (
    <div className="space-y-6">
      {/* Seletor de Modo de Upload */}
      <div className="flex justify-center">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
          <button
            onClick={() => setModoUpload('peticao')}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              modoUpload === 'peticao'
                ? 'bg-blue-500 text-white'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            Anexar Petição
          </button>
          <button
            onClick={() => setModoUpload('zip')}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              modoUpload === 'zip'
                ? 'bg-blue-500 text-white'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            Anexar Pasta (.zip)
          </button>
        </div>
      </div>

      {/* Área de Drop */}
      {status === 'aguardando_selecao' && (
        <div
          {...getRootProps()}
          className={`
            flex cursor-pointer flex-col items-center justify-center space-y-4
            rounded-lg border-2 border-dashed p-12 transition-colors
            ${
              isDragActive && !isDragReject
                ? 'border-blue-500 bg-blue-50'
                : isDragReject
                ? 'border-red-500 bg-red-50'
                : 'border-gray-300 bg-gray-50 hover:border-gray-400'
            }
          `}
        >
          <input {...getInputProps()} />
          
          <Upload
            className={`
              size-16
              ${isDragActive && !isDragReject ? 'text-blue-500' : 'text-gray-400'}
            `}
          />
          
          <div className="text-center">
            <p className="text-lg font-medium text-gray-700">
              {isDragActive && !isDragReject
                ? 'Solte o arquivo aqui...'
                : isDragReject
                ? 'Tipo de arquivo não permitido'
                : modoUpload === 'peticao'
                ? 'Arraste a petição inicial ou clique para selecionar'
                : 'Arraste a pasta compactada (.zip) ou clique para selecionar'}
            </p>
            <p className="mt-2 text-sm text-gray-500">
              {modoUpload === 'peticao'
                ? `Aceita apenas PDF e DOCX (máx ${TAMANHO_MAXIMO_PETICAO_MB}MB)`
                : `Aceita apenas arquivos ZIP (máx ${TAMANHO_MAXIMO_ZIP_MB}MB)`}
            </p>
          </div>
        </div>
      )}

      {/* Arquivo Selecionado */}
      {arquivoSelecionado && status !== 'aguardando_selecao' && (
        <div className="rounded-lg border bg-white p-4 shadow-sm">
          {/* Cabeçalho */}
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FileText className="size-8 text-blue-500" />
              <div>
                <p className="font-medium text-gray-900">{arquivoSelecionado.name}</p>
                <p className="text-sm text-gray-500">
                  {formatarTamanhoArquivo(arquivoSelecionado.size)}
                </p>
              </div>
            </div>
            
            <button
              onClick={removerArquivo}
              className="text-gray-400 transition-colors hover:text-red-500"
              aria-label="Remover arquivo"
            >
              <X className="size-5" />
            </button>
          </div>

          {/* Progress Bar */}
          {(status === 'processando_upload' || status === 'analisando_documentos') && (
            <div className="space-y-4">
              {/* Barra Global */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{etapaAtual}</span>
                  <span className="font-medium text-gray-900">{progressoUpload}%</span>
                </div>
                
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                  <div
                    className="h-full bg-blue-500 transition-all duration-300"
                    style={{ width: `${progressoUpload}%` }}
                  />
                </div>
              </div>
              
              {/* Barras Individuais por Arquivo (se houver) */}
              {Object.keys(progressoPorArquivo).length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">Progresso por arquivo:</h4>
                  {Object.entries(progressoPorArquivo).map(([nomeArquivo, dados]) => (
                    <div key={nomeArquivo} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="max-w-32 truncate text-gray-600" title={nomeArquivo}>
                          {nomeArquivo}
                        </span>
                        <span className="font-medium text-gray-900">{dados.progresso}%</span>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                        <div
                          className="h-full bg-green-500 transition-all duration-300"
                          style={{ width: `${dados.progresso}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-500">{dados.etapa}</div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Loader2 className="size-4 animate-spin" />
                <span>Processando em background...</span>
              </div>
            </div>
          )}

          {/* Enviando */}
          {status === 'enviando' && (
            <div className="flex items-center space-x-2 text-sm text-blue-600">
              <Loader2 className="size-4 animate-spin" />
              <span>Enviando arquivo...</span>
            </div>
          )}

          {/* Concluído */}
          {status === 'concluido' && (
            <div className="flex items-center space-x-2 text-sm text-green-600">
              <CheckCircle className="size-5" />
              <span>Upload e análise concluídos com sucesso!</span>
            </div>
          )}

          {/* Lista de Arquivos Extraídos (ZIP) */}
          {arquivosExtraidos.length > 0 && (
            <div className="mt-4">
              <h4 className="mb-2 text-sm font-medium text-gray-900">
                <span className="text-green-600">✓</span> Arquivos Processados ({arquivosExtraidos.length})
              </h4>
              <div className="max-h-40 overflow-y-auto rounded-lg border border-green-200 bg-green-50 p-3">
                <ul className="space-y-1">
                  {arquivosExtraidos.map((nomeArquivo, indice) => (
                    <li key={indice} className="flex items-center space-x-2 text-sm text-gray-700">
                      <FileText className="size-4 text-green-500" />
                      <span>{nomeArquivo}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Lista de Arquivos Ignorados (ZIP) */}
          {arquivosIgnorados.length > 0 && (
            <div className="mt-4">
              <h4 className="mb-2 text-sm font-medium text-gray-900">
                <span className="text-amber-600">⚠</span> Arquivos Ignorados ({arquivosIgnorados.length})
              </h4>
              <div className="max-h-40 overflow-y-auto rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="mb-2 text-xs text-amber-700">
                  Os seguintes arquivos foram ignorados por terem tipos não suportados:
                </p>
                <ul className="space-y-1">
                  {arquivosIgnorados.map((arquivo, indice) => (
                    <li key={indice} className="flex items-center space-x-2 text-sm text-amber-800">
                      <AlertCircle className="size-4 text-amber-500" />
                      <span className="font-medium">{arquivo.nome_arquivo}</span>
                      <span className="text-xs text-amber-600">({arquivo.extensao})</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-amber-600">
                  Tipos suportados: PDF, DOCX, PNG, JPG, JPEG
                </p>
              </div>
            </div>
          )}

          {/* Erro */}
          {status === 'erro' && (
            <div className="flex items-center space-x-2 text-sm text-red-600">
              <AlertCircle className="size-5" />
              <span>{mensagemErro}</span>
            </div>
          )}
        </div>
      )}

      {/* Erros de Validação */}
      {errosValidacao.length > 0 && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="mt-0.5 size-5 text-red-500" />
            <div className="flex-1">
              <p className="mb-2 font-medium text-red-900">
                Erros de Validação
              </p>
              <ul className="list-inside list-disc space-y-1">
                {errosValidacao.map((erro, indice) => (
                  <li key={indice} className="text-sm text-red-700">
                    {erro}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Documentos Sugeridos (Preview) removido para evitar bloqueio de navegação */}
    </div>
  );
}
